import React, { useCallback, useEffect, useState } from 'react';
import { getOrgMessengers, updateOrgMaxBot, updateOrgVkBot, updateOrgMailingRules } from '../../services/api';
import TelegramBotTab from './TelegramBotTab';
import BotProfilePanel from './BotProfilePanel';
import GuestRegistrationPanel from './GuestRegistrationPanel';
import WelcomeBonusPanel from './WelcomeBonusPanel';
import LoadingSpinner from '../common/LoadingSpinner';

const errorOf = (err, fallback) => err?.response?.data?.error || fallback;

/* Подключение по токену — одна форма для MAX и VK: сервер проверяет токен
   живым запросом перед сохранением, как и у Telegram. */
const TokenConnect = ({ title, hint, placeholder, status, onSave }) => {
  const [editing, setEditing] = useState(!status?.connected);
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setEditing(!status?.connected); }, [status?.connected]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await onSave(token.trim());
      setToken(''); setEditing(false);
    } catch (err) {
      setError(errorOf(err, 'Не удалось подключить'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel ui-settings-panel">
      <h4>{title}</h4>
      {status?.connected && !editing && (
        <>
          <div className="ui-status on">Подключён: {status.name}</div>
          <div className="ui-form-actions" style={{ justifyContent: 'flex-start', marginTop: 'var(--s-3)' }}>
            <button type="button" className="ui-btn-ghost" onClick={() => setEditing(true)}>Заменить токен</button>
          </div>
        </>
      )}
      {editing && (
        <form className="ui-form" onSubmit={submit} style={{ marginTop: 'var(--s-3)' }}>
          {error && <div className="ui-inline-error">{error}</div>}
          <div className="ui-settings-hint">{hint}</div>
          <div className="ui-field">
            <label>Токен</label>
            <input type="text" placeholder={placeholder} value={token} autoComplete="off"
                   onChange={(e) => setToken(e.target.value)} />
          </div>
          <div className="ui-form-actions">
            {status?.connected && (
              <button type="button" className="ui-btn-ghost" onClick={() => { setEditing(false); setError(''); }}>Отмена</button>
            )}
            <button type="submit" className="ui-btn-primary" disabled={busy || !token.trim()}>
              {busy ? 'Проверяем…' : 'Подключить'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

const MailingRules = ({ rules, onSaved }) => {
  const [form, setForm] = useState(rules);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => { setForm(rules); }, [rules]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setMessage(null);
    try {
      await updateOrgMailingRules(form);
      setMessage({ ok: true, text: 'Сохранено' });
      onSaved();
    } catch (err) {
      setMessage({ ok: false, text: errorOf(err, 'Не удалось сохранить') });
    } finally {
      setBusy(false);
    }
  };

  const num = (key) => (e) => setForm({ ...form, [key]: Number(e.target.value) });

  return (
    <form className="panel ui-settings-panel ui-form" onSubmit={submit}>
      <h4>Правила рассылок гостям</h4>
      <p className="ui-settings-hint">
        В тихие часы рассылки не отправляются: начатая вечером продолжится утром сама. Промежуток
        защищает гостей от лишних сообщений — частые рассылки чаще всего приводят к блокировке бота.
      </p>
      <div className="ui-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
        <div className="ui-field">
          <label>Тихие часы с</label>
          <input type="number" min="0" max="23" value={form.quiet_start} onChange={num('quiet_start')} />
        </div>
        <div className="ui-field">
          <label>до</label>
          <input type="number" min="0" max="23" value={form.quiet_end} onChange={num('quiet_end')} />
        </div>
        <div className="ui-field">
          <label>Не чаще раза в, дней</label>
          <input type="number" min="0" max="60" value={form.min_gap_days} onChange={num('min_gap_days')} />
        </div>
      </div>
      {message && <div className={message.ok ? 'ui-inline-success' : 'ui-inline-error'}>{message.text}</div>}
      <div className="ui-form-actions">
        <button type="submit" className="ui-btn-primary" disabled={busy}>Сохранить</button>
      </div>
    </form>
  );
};

const MessengersTab = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => getOrgMessengers().then(setData), []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <LoadingSpinner />;

  return (
    <>
      {data.telegram_health === 'down' && (
        <div className="ui-inline-error">
          Сообщения в Telegram сейчас не уходят: сервер не может связаться с Telegram. Это проблема
          связи сервера, а не вашего бота — переподключать его не нужно.
        </div>
      )}
      <TelegramBotTab />
      <TokenConnect
        title="Бот MAX"
        status={data.max}
        placeholder="Токен из кабинета партнёра MAX"
        hint={<>Создайте бота на <a href="https://business.max.ru" target="_blank" rel="noreferrer">business.max.ru</a>
          {' '}(раздел «Чат-боты»), скопируйте токен из настроек бота. Гости подписываются, поделившись контактом.</>}
        onSave={async (token) => { await updateOrgMaxBot(token); await load(); }}
      />
      <TokenConnect
        title="Сообщество ВКонтакте"
        status={data.vk}
        placeholder="vk1.a.…"
        hint={<>В сообществе: Управление → Работа с API → Ключи доступа → создать ключ с правами
          «Сообщения сообщества» и «Фотографии». Там же включите Long Poll API (последняя версия) и событие
          «Входящее сообщение», а в «Сообщениях» — «Возможности ботов». Номер в VK гость вводит сам, поэтому
          сообщения с именем и балансом туда не уходят.</>}
        onSave={async (token) => { await updateOrgVkBot(token); await load(); }}
      />
      <BotProfilePanel isAdmin />
      <GuestRegistrationPanel />
      <WelcomeBonusPanel />
      <MailingRules rules={data.rules} onSaved={load} />
    </>
  );
};

export default MessengersTab;
